<template lang="pug">
  .team-invite-members
    WtTitle(v-if="$auth.user()" card-title) Step 2: Invite Team Members
    .subtitle.mb-0(v-if="$auth.user()") {{ 'Team Size: '+ membersInTeam +' / '+ programSettingsCopy.maxTeamSize }}
      .subtitle.is-size-6(v-if="!isAdmin") • Note: you, the team creator, are automatically joined.
      .is-flex.mt-3(v-if="isAdmin")
        BTooltip.custom-tooltip(
          label="Admin Only Setting"
        )
          BIcon(icon='key')
        BCheckbox(
          v-model="addToTeam"
          :disabled="(!enableAdd && !addToTeam && isJoinedOtherTeam)"
        ) Add me to this team
    TeamBuilderAddMembersQuestions(v-if="$auth.user()" @actionSelected="newAction => this.action = newAction")
    .columns.members-tables(v-if="action && action !== 'skip'")
      .column.is-half
        MembersTable.fixed-table-layout(
          type="team"
          :loading="!membersLoaded"
          mode="api"
          :enable-search="true"
          @search="onSearch"
          :enable-add="enableAdd"
          :empty-message="membersLoaded && 'Awaiting members ...' || ''"
          :includeByType="action"
          :notInTeam="true"
          :inOrganizationId="selectedOrganizationId"
          :registrationBuilder="registrationBuilder"
          :registrationData="registrationData"
        )
      .column.is-half
        MembersTable.fixed-table-layout(
          type="team"
          mode="receiver"
          :enable-remove="true"
          :title="`Members To Be ${action === 'add' ? 'Added' : 'Invited'}`"
          :empty-message="membersLoaded && 'Awaiting members ...' || ''"
        )
    .subtitle.is-size-5(v-if="this.registrationBuilder") You can only invite members to the team after you log in
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import MembersTable from "@@/Members/MembersTable";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "../WtSvgIcon/WtSvgIcon";
import TeamBuilderAddMembersQuestions from "@@/TeamBuilder/TeamBuilderAddMembersQuestions";

const INVITE = "invite";
const ADD = "add";
const SKIP = "skip";

export default {
  name: "TeamBuilderAddMembers",

  components: {
    WtSvgIcon,
    WtTitle,
    MembersTable,
    TeamBuilderAddMembersQuestions
  },

  props: {
    selectedOrganizationId: {
      type: Number,
      default: 0
    },
    registrationBuilder: {
      type: Boolean,
      default: false
    },
    registrationData: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      action: null,
      addToTeam: true
    };
  },

  computed: {
    ...mapGetters("program", ["programSettingsCopy", "membersLoaded"]),
    ...mapGetters("challengeBuilder", ["stagedMembers"]),
    ...mapGetters("authMember", ["isAdmin"]),
    ...mapGetters("members", ["memberTeams"]),

    enableAdd() {
      // add one from staged members to account for the creator who is automatically part of team
      return (
        this.stagedMembers.length + (this.addToTeam ? 1 : 0) <
        this.programSettingsCopy.maxTeamSize
      );
    },

    membersInTeam() {
      return this.stagedMembers.length + (this.addToTeam ? 1 : 0);
    },

    isJoinedOtherTeam() {
      if (
        this.memberTeamData &&
        typeof this.memberTeamData === "object" &&
        !Array.isArray(this.memberTeamData)
      ) {
        if (Object.keys(this.memberTeamData).length > 0) {
          return true;
        }
      }
      return false;
    },

    memberTeamData() {
      return this._.isEmpty(this.memberTeams) &&
        this._.has(this.$auth.user(), "teams")
        ? this.$auth.user().teams[0]
        : this.memberTeams[0];
    }
  },

  watch: {
    action() {
      if (this.action) {
        this.clearSelectedEntitiesAndEnableNext();
      }
      switch (this.action) {
        case INVITE:
          this.updateTeamMembersInviteMode("invite");
          break;
        case ADD:
          this.updateTeamMembersInviteMode("add");
          break;
        case SKIP:
          this.updateTeamMembersInviteMode("skip");
          break;
      }
    },

    addToTeam() {
      this.updateAddCreatorToTeam(this.addToTeam);
    }
  },

  async mounted() {
    if (this.isAdmin) {
      this.addToTeam = false;
    }
    this.updateAddCreatorToTeam(this.addToTeam);
    this.onSearch = this._.debounce(async query => {
      const response = await this.searchMembers({
        programId: this.$auth.user().programId,
        query: query,
        perPage: 10,
        notInTeam: 1,
        inOrganizationId: this.selectedOrganizationId
      });

      this.setMembersResponseObject(response);
    }, 500);
    this.registrationBuilder
      ? this.$emit("disableNext", false)
      : this.$emit("disableNext", true);
  },

  methods: {
    ...mapActions("program", ["searchMembers", "setMembersResponseObject"]),
    ...mapActions("teams", [
      "updateAddCreatorToTeam",
      "updateTeamMembersInviteMode"
    ]),
    ...mapActions("challengeBuilder", ["emptyStagedMembers"]),

    onSearch() {
      // defining this here, but is overwritten in the
      // mounted call.  Using debounce requires this
      // approach in order for it to work when
      // the component instance is available.
    },

    clearSelectedEntitiesAndEnableNext() {
      this.emptyStagedMembers();
      this.$emit("disableNext", false);
    }
  }
};
</script>

<style scoped lang="sass">
.team-invite-members
  .members-tables
    min-height: 550px
.fixed-table-layout /deep/ table
  table-layout: fixed
.custom-tooltip
  padding-right: 10px
  /deep/ .tooltip-trigger
    display: flex
    align-items: center
</style>
